import { Injectable, signal } from "@angular/core";

@Injectable({
  providedIn: 'root'
})
export class IndexedDBService {
  private readonly dbName = "controlcar";

  private db: IDBDatabase;

  private readonly OBJECT_STORES = [
    { name: 'sort', keyPath: 'id' },
    { name: 'columns', keyPath: 'id' },
    { name: 'background_notifications', keyPath: 'id' },
    { 
      name: 'chats', keyPath: 'id', indexes: [{
        name: 'document',
        keyPath: ['document_type', 'document_id']
      }]
    },
    { name: 'syncs', keyPath: 'url' },
    { name: 'messages', keyPath: 'id', indexes: [{
      name: 'commentable',
      keyPath: ['commentable_type','commentable_id']
    }] },
  
  ];

  public isUpgrading = signal(false);

  constructor() {
    this.setupDB()
  }

  setupDB() {
    const request = indexedDB.open(this.dbName, 20);
    request.onblocked = () => {
      console.error('IndexedDB upgrade blocked. Please close other tabs using this app.');
    };
    request.onupgradeneeded = (event) => {
      this.isUpgrading.set(true);
      console.log('IndexedDB upgrade started');
      this.db = request.result;
      this.OBJECT_STORES.forEach((objectStore) => {
        if(!this.db.objectStoreNames.contains(objectStore.name)){
          console.log(`Creating object store: ${objectStore.name}`);
          const store = this.db.createObjectStore(objectStore.name, { keyPath: objectStore.keyPath });
          if (objectStore.indexes) {
            objectStore.indexes.forEach((index) => {
              console.log(`Creating index '${index.name}' on store '${objectStore.name}'`);
              store.createIndex(index.name, index.keyPath, { unique: false });
            });
          }
        }
        else{
          // Use the upgrade transaction to access the store
          const upgradeTransaction = (event.target as IDBOpenDBRequest).transaction as IDBTransaction;
          const store = upgradeTransaction.objectStore(objectStore.name);
          if (objectStore.indexes) {
            objectStore.indexes.forEach((index) => {
              if (!store.indexNames.contains(index.name)){
                console.log(`Creating index '${index.name}' on existing store '${objectStore.name}'`);
                store.createIndex(index.name, index.keyPath, { unique: false });
                console.log(`Índice '${index.name}' creado.`);
              }
            });
          }
        }
      });
      console.log('IndexedDB upgrade finished');
    };

    request.onsuccess = () => {
      this.db = request.result;
      this.isUpgrading.set(false);
    };
    request.onerror = ev => {
      this.isUpgrading.set(false);
      console.log(ev);
    };
  }

  write(objectStoreName: string, data: any) {
    if (!this.db) {
      console.error('Database not ready');
      return;
    }

    return this.db
      .transaction(objectStoreName, 'readwrite')
      .objectStore(objectStoreName)
      .put(data)
  }

  read(objectStoreName: string, key?: string | number | any[], index?:string): Promise<any> {
    return new Promise((resolve, reject) => {
        if (!this.db) {
          reject('Database not ready');
          return;
        }

        let query;
        if (key && index) {
          query = this.db.transaction(objectStoreName,'readonly').objectStore(objectStoreName).index(index).getAll(key);
        }
        else if (key ) {
          query = this.db.transaction(objectStoreName,'readonly').objectStore(objectStoreName).get(key);
        }
        else{
          query = this.db.transaction(objectStoreName,'readonly').objectStore(objectStoreName).getAll();
        }

        query.onsuccess = ev => {
          resolve(query.result);
        };
        query.onerror = () => {
          reject(query.error);
        };
    });
  }

  delete(objectStoreName: string, key: string | number) {
    if (!this.db) {
      console.error('Database not ready');
      return;
    }

    this.db
      .transaction(objectStoreName, 'readwrite')
      .objectStore(objectStoreName)
      .delete(key);
  }

  softDelete(objectStoreName: string, key: string | number, newData: any) {
    if (!this.db) {
      console.error('Database not ready');
      return;
    }

    const request = this.db
      .transaction(objectStoreName, 'readwrite')
      .objectStore(objectStoreName)
      .get(key);
    request.onsuccess = () => {
      const data = request.result;
      if (data) {
        
        this.db
          .transaction(objectStoreName, 'readwrite')
          .objectStore(objectStoreName)
          .put({
            ...data,
            deleted: true,
            ...newData
          });
      }

    }
    request.onerror = () => {
      console.error('Error reading data', request.error);
    };
  }

  clear(objectStoreName: string) {
    if (!this.db) {
      console.error('Database not ready');
      return;
    }

    this.db
      .transaction(objectStoreName, 'readwrite')
      .objectStore(objectStoreName)
      .clear();
  }
  clearAllData() {  
    if (!this.db) {
      console.error('Database not ready');
      return;
    }
    this.OBJECT_STORES.forEach((objectStore) => {
      this.clear(objectStore.name);
    });
  }

}
